import $console from '@zuto/utilities/src/$console'
import MarketingHelper from '@zuto-internal/zuto-marketing-helper'
import { AttributionDetails } from '../components/ApplicationForm/types'
import { WafBotLabels } from '../utils/getServerSideProps/getWafBotLabels'

export type AppFormEventParams = {
  CustomerId?: string
  PartnerCustomerId?: string
  QuoteId?: string
  Prefill: boolean
  Lender?: string
  PreApproved?: boolean
  Source?: string
  Medium?: string
  Campaign?: string
  DeviceId?: string
  SessionId?: string
  Experiments?: string
  Journey?: string
  Attribution?: AttributionDetails
  Wbraid?: string
  Gbraid?: string
  Fbclid?: string
  WafBotLabels?: WafBotLabels
}

export type AppFormNavigationType =
  | 'AppFormServiceExtensionNavigation'
  | 'AppFormNextDataRequest'
  | 'AppFormRangeRequest'
  | 'AppFormRedirect_QUERY_NORMALISATION'
  | 'AppFormRedirect_TRAILING_SLASH'
  | 'AppFormPrefetchedOrPrerendered'
  | 'AppFormRealUserNavigation'
  | 'AppFormNonHtmlRequest'
  | 'AppFormSpeculativeFetch'
  | 'AppFormNonRealUserNavigation'
export type AppFormCustomEventType = 'ContinueToMyZutoCta' | 'PhoneCta' | 'WhatsappCta' | 'SmsCta' | 'AppFormRefreshed' | 'AppFormNavigation' | 'AppFormQuestionVisible'
export type AppFormCustomEventMeta = Record<string, string | number | boolean | null | undefined | string[]>

export interface EventsServiceResult {
  sendAppFormStartedEvent: () => Promise<void>
  sendAppFormSubmittedEvent: (success: boolean, isRetry: boolean) => Promise<void>
  sendEvent: (eventType: AppFormCustomEventType, meta?: AppFormCustomEventMeta) => void
}

const EventsService = (eventsApi: string, params: AppFormEventParams): EventsServiceResult => {
  if (!eventsApi) {
    throw new Error('Missing eventsApi URL')
  }

  const buildCommonEventParameters = (eventType: string) => {
    const channelAttribution = params.Attribution ? params.Attribution : MarketingHelper.getChannelAttribution()

    return {
      EventSource: 'AppForm',
      EventCategory: 'AppForm',
      Event: eventType,
      DeviceId: params.DeviceId,
      SessionId: params.SessionId,
      Source: (channelAttribution && channelAttribution.source) || 'Direct',
      Medium: (channelAttribution && channelAttribution.medium) || 'NoAttribution',
      Campaign: channelAttribution && channelAttribution.campaign ? channelAttribution.campaign : 'Zuto',
      CustomerId: params.CustomerId,
      PartnerCustomerId: params.PartnerCustomerId,
      Prefill: params.Prefill !== undefined ? `${params.Prefill ? '1' : '0'}` : undefined,
      Eligibility: null, // we don't get this from the Prospective Applicants API
      Lender: params.Lender,
      Preapproved: params.PreApproved !== undefined ? `${params.PreApproved ? '1' : '0'}` : undefined,
      QuoteId: params.QuoteId,
      Experiments: params.Experiments,
      Journey: params.Journey,
      ...(params.WafBotLabels && {
        WafBotCategory: params.WafBotLabels.category,
        WafBotSignal: params.WafBotLabels.signal,
        WafBotVerified: params.WafBotLabels.verified ? '1' : '0'
      })
    }
  }

  const fireAndForget = (url: string, params: Record<string, any>) => {
    const controller = new AbortController()
    const timer = setTimeout(() => controller.abort(), 1500)

    const normalized: Record<string, string> = Object.fromEntries(Object.entries(params).map(([k, v]) => [k, String(v)]))

    const query = new URLSearchParams(normalized).toString()

    try {
      // keepalive lets the request continue during page unload/redirect
      fetch(`${url}?${query}`, { method: 'GET', keepalive: true, signal: controller.signal })
    } catch {
      // swallow: logging is fine if you want, but never throw
      $console.error(`FireAndForget telemetry failed: ${params.EventType}`)
    } finally {
      clearTimeout(timer)
    }
  }

  const sendAppFormStartedEvent = async () => {
    const commonEventParams = buildCommonEventParameters('AppFormStarted')
    fireAndForget(eventsApi, { ...commonEventParams, ...{ EventType: 'AppFormStarted' } })
  }

  const sendAppFormSubmittedEvent = async (success: boolean, isRetry: boolean) => {
    const commonEventParams = buildCommonEventParameters('AppFormSubmitted')

    fireAndForget(eventsApi, {
      ...commonEventParams,
      EventType: 'AppFormSubmitted',
      Success: success ? '1' : '0',
      IsRetry: isRetry ? '1' : '0'
    })
  }

  const sendEvent = (eventType: AppFormCustomEventType, meta: AppFormCustomEventMeta = {}) => {
    const commonEventParams = buildCommonEventParameters(eventType)

    const cleanedMeta = Object.fromEntries(Object.entries(meta).filter(([, v]) => v !== undefined))

    fireAndForget(eventsApi, {
      ...commonEventParams,
      EventType: eventType,
      ...cleanedMeta
    })
  }

  return {
    sendAppFormStartedEvent,
    sendAppFormSubmittedEvent,
    sendEvent
  }
}

export default EventsService
